import { useEffect, useState } from 'react';
import type { Profesional } from '../../../api/ops/citas';
import { Boton } from '../../../componentes/Boton';
import { Chip } from '../../../componentes/Chip';
import { EstadoCargando, EstadoError } from '../../../componentes/EstadoPagina';
import { Filtros } from '../../../componentes/Filtros';
import { Icono } from '../../../componentes/Icono';
import { Tabla } from '../../../componentes/Tabla';
import { estadoDeError } from '../../../logica/estados';
import { iniciales } from '../../../logica/navegacion';
import { useCabecera } from '../../../marco/cabecera';
import { Avatar } from '../../../marco/piezas';
import { usePermisos } from '../../../permisos/permisos';
import type { PropsPantalla } from '../../tipos';
import { TarjetaAnillo } from '../Anillo';
import { useDatosServicios } from '../datos';
import { AYUDA, PERMISOS, SOLO_LECTURA } from '../textos';
import { FichaProfesional } from './FichaProfesional';
import { ACCESO, esPestanaProfesional, horarioDe, serviciosDe, type Horarios } from './logica';

type Filtro = 'activos' | 'desactivados';

/**
 * 9.6 «Profesionales» (apartado de «Servicios y profesionales», `?apartado=profesionales`, ficha `&profesional={id}&tab=`):
 * el límite del plan en RV Anillo (G10, G481, G482: la base trae 1 profesional activo y cada «Profesional extra», uno más),
 * la lista de activos y desactivados con sus servicios, su horario (el del negocio o uno propio) y su acceso al panel, y la
 * ficha (`FichaProfesional`). «Contratar profesional extra» solo lo ve el propietario; mientras el cobro no esté en la API
 * (`COBRO_DISPONIBLE` en `plan/cobro.ts`), lleva a Plan con el bloque marcado (G120).
 */
export function Profesionales(props: PropsPantalla) {
    const { ops, negocio, movil, soloLectura, ruta, busqueda, navegar, membresia } = props;
    const { puede } = usePermisos();
    const gestiona = puede(PERMISOS.profesionales) && !soloLectura;
    const propietario = membresia?.is_owner === true;
    const { datos, error, reintentar, ponerProfesional } = useDatosServicios(ops, negocio, puede);
    const [filtro, setFiltro] = useState<Filtro>('activos');
    const [horarios, setHorarios] = useState<Horarios>({});
    const abierto = busqueda.get('profesional');
    const tab = busqueda.get('tab');
    const base = `${ruta}?apartado=profesionales`;
    const abrir = (id: string | null, pestana?: string) => {
        navegar(id === null ? base : `${base}&profesional=${encodeURIComponent(id)}${pestana === undefined ? '' : `&tab=${pestana}`}`, true);
    };

    useCabecera({
        ayuda: AYUDA.profesionales,
        acciones: gestiona ? [{ clave: 'nuevo', texto: 'Nuevo profesional', icono: 'user-plus' as const, onClick: () => { abrir('nuevo'); } }] : [],
    });

    // Si cada uno usa el horario del negocio o uno propio (su semana tipo vigente).
    const calendarios = (datos?.profesionales ?? []).map((p) => p.calendar_id).join(',');
    useEffect(() => {
        if (datos === null || !puede(PERMISOS.verHorarios)) {
            return;
        }
        let vivo = true;
        Promise.all(
            datos.profesionales.map((p) =>
                ops
                    .listWeeklySchedules(negocio, p.calendar_id, { limit: 20 })
                    .then((l) => [p.id, l.data.find((s) => s.status === 'current')?.inherits === false ? 'propio' : 'negocio'] as const)
                    .catch(() => [p.id, null] as const),
            ),
        )
            .then((pares) => {
                if (vivo) {
                    setHorarios(Object.fromEntries(pares));
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
        // Se vuelve a leer cuando cambia la lista de calendarios.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, negocio, calendarios]);

    if (error !== null) {
        return <EstadoError seccion="tu equipo" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={reintentar} movil={movil} />;
    }
    if (datos === null) {
        return <EstadoCargando seccion="tu equipo" />;
    }

    const activos = datos.profesionales.filter((p) => p.status === 'active');
    const limite = datos.limite ?? activos.length;
    const lleno = activos.length >= limite;
    const lista = datos.profesionales.filter((p) => (filtro === 'activos') === (p.status === 'active'));
    const contratar = () => {
        // Contratar va por Plan con el bloque marcado (G120): el cobro de la cuota aún no está en la API (`COBRO_DISPONIBLE`).
        navegar('/plan?block=profesional_extra');
    };
    const profesional = abierto === null || abierto === 'nuevo' ? null : (datos.profesionales.find((p) => p.id === abierto) ?? null);
    const vacia = datos.profesionales.length === 0;

    return (
        <>
            {soloLectura && (
                <p className="rv-sv-nota">
                    <Icono nombre="eye" />
                    <span>{SOLO_LECTURA}</span>
                </p>
            )}
            {datos.limite !== null && (
                <TarjetaAnillo
                    valor={activos.length}
                    tope={limite}
                    etiqueta="Profesionales activos"
                    detalle={`${String(activos.length)} de ${String(limite)} en tu plan`}
                    nota={`${lleno ? 'Plan completo: para activar otro, contrata un profesional extra. ' : ''}La base incluye 1 y tienes ${String(datos.extras)} ${datos.extras === 1 ? 'profesional extra' : 'profesionales extra'}. Los desactivados no cuentan.`}
                >
                    {propietario && !soloLectura ? (
                        <Boton tipo="secundario" icono="user-plus" movil={movil} onClick={contratar}>
                            Contratar profesional extra
                        </Boton>
                    ) : gestiona && lleno ? (
                        <span className="rv-sv-nota">
                            <Icono nombre="lock" />
                            <span>Pídeselo al propietario: solo él puede añadir profesionales extra al plan.</span>
                        </span>
                    ) : null}
                </TarjetaAnillo>
            )}
            {vacia ? (
                <div className="rv-sv-vacia">
                    <span className="rv-sv-baldosa" style={{ background: 'var(--rv-ink)' }}>
                        <Icono nombre="contact" />
                    </span>
                    <strong>Todavía no hay profesionales</strong>
                    <span>Da de alta a quien atiende: con sus servicios ya puede recibir citas.</span>
                </div>
            ) : (
                <>
                    <Filtros
                        etiqueta="Estado de los profesionales"
                        items={[
                            { etiqueta: 'Activos', cifra: activos.length },
                            { etiqueta: 'Desactivados', cifra: datos.profesionales.length - activos.length },
                        ]}
                        valor={filtro === 'activos' ? 0 : 1}
                        onCambio={(i) => { setFiltro(i === 0 ? 'activos' : 'desactivados'); }}
                    />
                    <TablaProfesionales lista={lista} filtro={filtro} datos={datos.servicios} horarios={horarios} movil={movil} onAbrir={(id) => { abrir(id); }} />
                </>
            )}
            {(abierto === 'nuevo' && gestiona) || profesional !== null ? (
                <FichaProfesional
                    key={abierto ?? ''}
                    {...props}
                    datos={datos}
                    profesional={profesional}
                    horario={profesional === null ? null : (horarios[profesional.id] ?? null)}
                    pestana={esPestanaProfesional(tab) ? tab : 'datos'}
                    gestiona={gestiona}
                    propietario={propietario}
                    onContratar={contratar}
                    onPestana={(p) => { abrir(profesional?.id ?? null, p); }}
                    onCerrar={() => { abrir(null); }}
                    onCreado={(p) => {
                        ponerProfesional(p);
                        setFiltro(p.status === 'active' ? 'activos' : 'desactivados');
                        abrir(null);
                    }}
                    onCambiado={(p) => {
                        ponerProfesional(p);
                        if (p.status !== profesional?.status) {
                            setFiltro(p.status === 'active' ? 'activos' : 'desactivados');
                        }
                    }}
                    onHorarios={(id) => { navegar(`${ruta}?apartado=horarios&profesional=${encodeURIComponent(id)}`); }}
                />
            ) : null}
        </>
    );
}

function TablaProfesionales({ lista, filtro, datos, horarios, movil, onAbrir }: { lista: Profesional[]; filtro: Filtro; datos: Parameters<typeof serviciosDe>[1]; horarios: Horarios; movil: boolean; onAbrir: (id: string) => void }) {
    const vacia = (
        <div className="rv-sv-vacia" style={{ boxShadow: 'none' }}>
            <strong>{filtro === 'activos' ? 'No hay profesionales activos' : 'No hay profesionales desactivados'}</strong>
            <span>{filtro === 'activos' ? 'Activa a alguien desde su ficha para que reciba citas.' : 'Los que desactives siguen aquí, con su historial.'}</span>
        </div>
    );
    return (
        <Tabla
            etiqueta="Profesionales"
            movil={movil}
            vacia={vacia}
            columnas={[
                { clave: 'pro', titulo: 'Profesional', ancho: 'minmax(220px, 1.6fr)' },
                { clave: 'svcs', titulo: 'Servicios', ancho: 'minmax(160px, 1.4fr)' },
                { clave: 'hor', titulo: 'Horario', ancho: '140px' },
                { clave: 'acc', titulo: 'Acceso', ancho: '160px' },
                { clave: 'estado', titulo: 'Estado', ancho: '120px' },
            ]}
            filas={lista.map((p) => {
                const sv = serviciosDe(p, datos).map((s) => s.current_version.name);
                const acc = ACCESO[p.panel_access];
                const avatar = <Avatar texto={iniciales(p.display_name)} tamano={36} fondo={p.color ?? 'var(--rv-ink)'} tinta="#fff" />;
                const textoServicios = sv.length === 0 ? 'Ninguno' : `${String(sv.length)} · ${sv.slice(0, 2).join(', ')}${sv.length > 2 ? '…' : ''}`;
                const hor = horarioDe(horarios[p.id]);
                return {
                    clave: p.id,
                    etiqueta: p.display_name,
                    onAbrir: () => { onAbrir(p.id); },
                    celdas: {
                        pro: (
                            <span className="rv-sv-celda">
                                {p.photo_url != null ? <img className="rv-sv-foto" src={p.photo_url} alt="" width={36} height={36} /> : avatar}
                                <span className="rv-sv-celda__texto">
                                    <strong>{p.display_name}</strong>
                                    <small>{p.email ?? p.phone ?? 'Sin contacto'}</small>
                                </span>
                            </span>
                        ),
                        svcs: textoServicios,
                        hor,
                        acc: p.panel_access === 'granted' ? acc.texto : <Chip estado={acc.chip}>{acc.texto}</Chip>,
                        estado: p.status === 'active' ? '' : <Chip estado="neutral">Desactivado</Chip>,
                    },
                    tarjeta: (
                        <span className="rv-sv-celda">
                            {avatar}
                            <span className="rv-sv-celda__texto">
                                <strong>{p.display_name}</strong>
                                <small>{[sv.length === 1 ? '1 servicio' : `${String(sv.length)} servicios`, p.status === 'active' ? `horario ${hor.toLocaleLowerCase('es')}` : 'desactivado'].join(' · ')}</small>
                                <span className="rv-sv-chips">
                                    {p.status !== 'active' && <Chip estado="neutral">Desactivado</Chip>}
                                    {p.panel_access !== 'granted' && <Chip estado={acc.chip}>{acc.texto}</Chip>}
                                </span>
                            </span>
                        </span>
                    ),
                };
            })}
        />
    );
}
